import React from 'react';
import { FUNNEL_TYPES } from '../lib/data';
import { OUTCOME_EDUCATION } from '../lib/outcomeEducation';
import { Button } from './ui/Button';
import { HelpCircleIcon, PlayIcon, ArrowRightIcon, BookOpenIcon } from 'lucide-react';
import { Card, CardContent } from './ui/Card';
import type { OutcomeEducation } from '../lib/outcomeEducation';
function EducationCard({
  education
}: {
  education: OutcomeEducation;
}) {
  const Icon = education.icon;
  const recommendedTypes = FUNNEL_TYPES.filter(ft => education.recommendedFunnelTypes.includes(ft.id));
  return <Card className="border-blue-200 bg-blue-50/30 overflow-hidden" data-id="element-81">
      <CardContent className="p-5 md:p-6" data-id="element-82">
        <div className="flex items-center gap-3 mb-4" data-id="element-83">
          <div className="p-2 bg-blue-600 text-white rounded-lg" data-id="element-84">
            <Icon className="w-5 h-5" data-id="element-85" />
          </div>
          <h3 className="text-base font-bold text-foreground" data-id="element-86">
            {education.headline}
          </h3>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed mb-5" data-id="element-87">
          {education.description}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-88">
          {/* Recommended Funnel Types */}
          <div data-id="element-89">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2" data-id="element-90">
              <ArrowRightIcon className="w-4 h-4 text-blue-600" data-id="element-91" />
              Recommended Funnel Types
            </h4>
            <div className="space-y-2" data-id="element-92">
              {recommendedTypes.map(ft => {
              const FtIcon = ft.icon;
              return <div key={ft.id} className="flex items-start gap-3 bg-background p-3 rounded-lg border border-border" data-id="element-93">
                    <div className="p-1.5 bg-muted rounded-md flex-shrink-0" data-id="element-94">
                      <FtIcon className="w-4 h-4 text-muted-foreground" data-id="element-95" />
                    </div>
                    <div data-id="element-96">
                      <span className="text-sm font-medium text-foreground" data-id="element-97">
                        {ft.name}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5" data-id="element-98">
                        {ft.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Video Placeholder */}
          <div className="bg-slate-900 rounded-lg border border-border overflow-hidden flex flex-col items-center justify-center aspect-video relative group cursor-pointer" data-id="element-99">
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-slate-900/40" data-id="element-100" />
            <div className="relative z-10 flex flex-col items-center gap-3" data-id="element-101">
              <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-colors" data-id="element-102">
                <PlayIcon className="w-6 h-6 text-white ml-0.5" data-id="element-103" />
              </div>
              <span className="text-sm font-medium text-white/90" data-id="element-104">
                Watch how it works
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>;
}
interface FunnelTypeGuideProps {
  selectedOutcome: string | null;
  onOpenQuiz: () => void;
}
export function FunnelTypeGuide({
  selectedOutcome,
  onOpenQuiz
}: FunnelTypeGuideProps) {
  // Find education content for selected outcome
  const activeEducation = selectedOutcome ? OUTCOME_EDUCATION.find(e => e.outcome === selectedOutcome) ?? null : null;
  // Default state — no outcome selected
  if (!activeEducation) {
    return <div className="mb-8" data-id="element-105">
        <Card className="border-border bg-background shadow-sm" data-id="element-106">
          <CardContent className="p-6 md:p-8" data-id="element-107">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-id="element-108">
              <div className="flex items-start gap-4" data-id="element-109">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-xl flex-shrink-0" data-id="element-110">
                  <BookOpenIcon className="w-6 h-6" data-id="element-111" />
                </div>
                <div data-id="element-112">
                  <h2 className="text-lg font-semibold text-foreground mb-1" data-id="element-113">
                    What do you want to do?
                  </h2>
                  <p className="text-sm text-muted-foreground max-w-lg" data-id="element-114">
                    Select an outcome from the sidebar to learn which funnel
                    types can help you achieve it — or let us help you figure it
                    out.
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={onOpenQuiz} className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50 flex-shrink-0" data-id="element-115">
                <HelpCircleIcon className="w-4 h-4" data-id="element-116" />
                Help Me Choose
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="mb-8 space-y-4" data-id="element-117">
      <div className="flex items-center justify-between" data-id="element-118">
        <h2 className="text-lg font-semibold text-foreground" data-id="element-119">
          Learn about your selection
        </h2>
        <Button variant="outline" size="sm" onClick={onOpenQuiz} className="gap-2 text-blue-600 border-blue-200 hover:bg-blue-50" data-id="element-120">
          <HelpCircleIcon className="w-4 h-4" data-id="element-121" />
          Help Me Choose
        </Button>
      </div>
      <EducationCard education={activeEducation} data-id="element-122" />
    </div>;
}